import React, { useState, useMemo, useEffect } from 'react';
import type { SortOrderScreen as SortOrderScreenType } from '../../types/course';
import { playTickSound, playCorrectSound, playIncorrectSound } from '../../utils/sound';

interface Props {
  screen: SortOrderScreenType;
  onInteractionComplete: () => void;
  completed: boolean;
}

export const SortOrderScreen: React.FC<Props> = ({
  screen,
  onInteractionComplete,
  completed,
}) => {
  // Items placed in order so far
  const [placed, setPlaced] = useState<string[]>(
    () => completed ? [...screen.items] : []
  );
  const [wrongFlash, setWrongFlash] = useState(false);

  // Remaining items shown scrambled
  const remaining = useMemo(() => {
    const placedSet = new Set(placed);
    const items = screen.items.filter((item) => !placedSet.has(item));
    // Deterministic shuffle
    const seed = screen.prompt.length;
    return items.sort((a, b) => {
      const ha = (a.charCodeAt(0) * 31 + a.length + seed) % 100;
      const hb = (b.charCodeAt(0) * 31 + b.length + seed) % 100;
      return ha - hb;
    });
  }, [screen.items, placed, screen.prompt]);

  const allPlaced = placed.length === screen.items.length;

  useEffect(() => {
    if (allPlaced && !completed) {
      onInteractionComplete();
      playCorrectSound();
    }
  }, [allPlaced, completed, onInteractionComplete]);

  const handleTap = (item: string) => {
    const nextCorrect = screen.items[placed.length];
    if (item === nextCorrect) {
      playTickSound();
      setPlaced((prev) => [...prev, item]);
      setWrongFlash(false);
    } else {
      playIncorrectSound();
      setWrongFlash(true);
      setTimeout(() => setWrongFlash(false), 600);
    }
  };

  return (
    <div className="flex flex-col gap-4 px-1">
      {screen.icon && <div className="text-3xl text-center">{screen.icon}</div>}
      <h3 className="text-lg font-semibold font-serif text-[var(--clue-text-primary)] text-center">
        {screen.title}
      </h3>

      {screen.body && (
        <>
          <p className="text-sm leading-relaxed text-[var(--clue-text-secondary)]">
            {screen.body}
          </p>
          <div className="flex items-center gap-2 mt-1">
            <div className="flex-1 h-px bg-[var(--clue-border-mid)]" />
            <span className="text-[10px] font-semibold text-[var(--clue-text-tertiary)] uppercase tracking-widest">Your turn</span>
            <div className="flex-1 h-px bg-[var(--clue-border-mid)]" />
          </div>
        </>
      )}

      <p className="text-sm text-[var(--clue-text-secondary)] text-center">
        {screen.prompt}
      </p>

      {/* Placed items */}
      {placed.length > 0 && (
        <div className="space-y-1.5">
          {placed.map((item, i) => (
            <div
              key={item}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--clue-green-accent)]/10 border border-[var(--clue-green-accent)]/30"
            >
              <span className="w-5 h-5 rounded-full bg-[var(--clue-green-accent)] text-white flex items-center justify-center text-xs font-bold shrink-0">
                {i + 1}
              </span>
              <span className="text-sm text-[var(--clue-text-primary)]">{item}</span>
            </div>
          ))}
        </div>
      )}

      {/* Next slot indicator */}
      {!allPlaced && (
        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed transition-colors ${
          wrongFlash
            ? 'border-red-400 bg-red-500/10'
            : 'border-[var(--clue-green-accent)]/50 bg-[var(--clue-green-accent)]/5'
        }`}>
          <span className="w-5 h-5 rounded-full bg-[var(--clue-border-mid)] text-[var(--clue-text-tertiary)] flex items-center justify-center text-xs font-bold shrink-0">
            {placed.length + 1}
          </span>
          <span className="text-sm text-[var(--clue-text-tertiary)] italic">Tap the next item...</span>
        </div>
      )}

      {/* Remaining items (scrambled) */}
      {remaining.length > 0 && (
        <div className="flex flex-wrap gap-2 justify-center">
          {remaining.map((item) => (
            <button
              key={item}
              onClick={() => handleTap(item)}
              className="px-3 py-1.5 rounded-full border border-[var(--clue-border-mid)] bg-[var(--clue-bg-primary)] text-sm font-medium text-[var(--clue-text-primary)] hover:border-[var(--clue-green-accent)] hover:bg-[var(--clue-green-accent)]/10 transition-colors"
            >
              {item}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
